import { useStore } from 'hooks/useStore';
import {
  DropdownDetails,
  OpportunityDetail,
  Stage,
  UpdateBoardPayload,
  updateOpportunity,
} from 'Interfaces/BDPortalInterfaces';
import { DropResult } from 'react-beautiful-dnd';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSearchParamsState } from 'hooks/useSearchParamState';
import { deepCopy } from 'utils/deepCopy';

export const OpportunityKanbanBoardLogic = () => {
  const {
    UserStore: { userModulesRoles, userMetadata },
    BDPortalStore: {
      board,
      setBoard,
      filterAnalyst,
      filterAccManagers,
      allOpportunityDetails,
      searchKeyword,
      quickLoadCards,
      addInQuickLoad,
      removeFromQuickLoad,
      getAllOpportunity,
      getDropdownDetails,
      setAllOpportunityDetails,
      fetchDropdownBoardDetails,
      isOpportunityBoardDetailsLoading,
      handleFetchAllConceptDetailsForSearch,
      assigneeUpdatingCards,
      addInAssigneeUpdatingCards,
      removeFromAssigneeUpdatingCards,
      filterPrimarySupplier,
      filterSource,
      filterAgency,
      filterStage,
      filterStatus,
      filterIsPortfolioOpportunity,
      filterAnnualRevenueRange,
      filterStartAndEnddates,
    },
  } = useStore();
  const [boardDetails, setBoardDetails] = useState<DropdownDetails | null>(null);
  const [iteration, setIteration] = useState<string>(board);
  const [stageNames, setStageNames] = useState<string[]>([]);
  const [isEditOpportunityModalOpen, setIsEditOpportunityModalOpen] = useState(false);
  const [expandedStageNames, setExpandedStageNames] = useState<string[]>(
    stageNames.length ? [stageNames[0]] : []
  );
  const [opportunityIID, setOpportunityIID] = useState(0);
  const [quickLoad, _setQuickLoad] = useState(false);
  const [opportunityID, setOpportunityID] = useState<number | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [opportunityIdParam] = useSearchParamsState(
    'opportunityIdParam',
    ''
  );
  const [opportunityIterationIdParam] = useSearchParamsState(
    'opportunityIterationIdParam',
    ''
  );
  const [quickLoadCardsLocal, setQuickLoadCardsLocal] = useState<Set<number>>(new Set());

  const getExpandedStageNames = () => {
    const tempStageNames: string[] = [];
    if (
      (!filterAnalyst || filterAnalyst?.length === 0) &&
      !searchKeyword &&
      (!filterSource || filterSource?.length === 0) &&
      (!filterAgency || filterAgency?.length === 0) &&
      !filterPrimarySupplier &&
      filterIsPortfolioOpportunity == null &&
      (!filterStartAndEnddates ||
        (filterStartAndEnddates[0] === null && filterStartAndEnddates[1] === null)) &&
      (!filterAnnualRevenueRange ||
        (filterAnnualRevenueRange[0] === null && filterAnnualRevenueRange[1] === null))
    ) {
      return [];
    }

    if (boardDetails) {
      boardDetails?.[board as 'NC' | 'MAS']?.Stage?.forEach(stage => {
        boardDetails?.[board as 'NC' | 'MAS']?.Status?.forEach(status => {
          if (
            filterOpportunityIterationsForTheappliedFilters(
              allOpportunityDetails,
              stage.ID,
              status.ID
            ).length > 0
          ) {
            tempStageNames.push(stage.Stage);
          }
        });
      });
    }
    return Array.from(new Set(tempStageNames));
  };

  const [updateBoardPayload, setUpdateBoardPayload] = useState<UpdateBoardPayload>({
    OpportunityIterationsID: null,
    StageID: 1,
    StatusID: 1,
    User: '',
    AnalystID: null,
  });

  useEffect(() => {
    // if both the params are in the url open the edit opportunity modal
    if (opportunityIdParam && opportunityIterationIdParam) {
      setOpportunityID(parseInt(opportunityIdParam));
      setOpportunityIID(parseInt(opportunityIterationIdParam));
      setIsEditOpportunityModalOpen(true);
    }
  }, [opportunityIdParam, opportunityIterationIdParam]);

  useEffect(() => {
    handleGetDropdownDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkIfCardIsGettingAssignedUpdated = (opportunityID: number) => {
    return assigneeUpdatingCards.has(opportunityID);
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    fetchDropdownBoardDetails(signal);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board]);

  useEffect(() => {
    if (stageNames.length > 0) {
      setIteration(board);
      setExpandedStageNames([stageNames[0]]);
    } else {
      setExpandedStageNames([]);
    }
  }, [stageNames, board]);

  useEffect(() => {
    setQuickLoadCardsLocal(quickLoadCards);
  }, [quickLoadCards]);

  useEffect(() => {
    setExpandedStageNames(getExpandedStageNames());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchKeyword,
    filterAnalyst,
    filterAccManagers,
    filterAgency,
    filterAnnualRevenueRange,
    filterSource,
    filterIsPortfolioOpportunity,
    filterStartAndEnddates,
    filterPrimarySupplier,
    boardDetails,
    board,
    allOpportunityDetails,
  ]);

  const handleGetDropdownDetails = async () => {
    try {
      const response = await getDropdownDetails();
      setBoardDetails(response!);
      if (response) {
        if (board === 'NC' && response.NC) {
          setStageNames(response.NC.Stage.map((stageObj: Stage) => stageObj.Stage));
        } else if (board === 'MAS' && response.MAS) {
          setStageNames(response.MAS.Stage.map((stageObj: Stage) => stageObj.Stage));
        } else {
          setStageNames([]);
        }
      }
    } catch (error) {
      console.error('Error fetching dropdown details:', error);
    }
  };

  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const [sourceStageId, _sourceStatusId] = source.droppableId.split('|');
    const [destStageId, destStatusId] = destination.droppableId.split('|');

    const movedOpportunity = allOpportunityDetails.find(
      opportunity => opportunity.OpportunityIterationsID === parseInt(draggableId)
    );

    if (!movedOpportunity) return;

    const sourceStage = parseInt(sourceStageId);
    const destStage = parseInt(destStageId);
    const destStatus = parseInt(destStatusId);

    const isValidMove = () => {
      // check whether the user
      if (!userMetadata) return false;

      if (
        userMetadata?.userInfo.deptId.departmentName.toLocaleLowerCase() === 'developer' ||
        userMetadata?.userInfo.deptId.departmentName.toLocaleLowerCase() === 'management'
      ) {
        return true;
      }

      if (userMetadata?.roles.opptracker.length === 0) {
        return false;
      }

      if (
        userMetadata.roles.opptracker[0].roleId.roleCode.toLocaleLowerCase() === 'admin' ||
        userMetadata.roles.opptracker[0].roleId.roleCode.toLocaleLowerCase() === 'super'
      ) {
        return true;
      }

      if (sourceStage === destStage) {
        return true;
      }
      let sourceIndex: number = -1;
      let targetIndex: number = -1;

      if (board === 'MAS') {
        boardDetails?.MAS.Stage.forEach((value, index) => {
          if (sourceStage === value.ID) {
            sourceIndex = index;
          }
          if (destStage === value.ID) {
            targetIndex = index;
          }
        });
      } else {
        boardDetails?.NC.Stage.forEach((value, index) => {
          if (sourceStage === value.ID) {
            sourceIndex = index;
          }
          if (destStage === value.ID) {
            targetIndex = index;
          }
        });
      }
      // Note: sourceIndex and targetIndex can be used for more complex validation logic
      // Currently we check if both stages are in the first 3 stages (indices 0-2)
      if (sourceIndex <= 2 && targetIndex <= 2) return true;
      if (Number(sourceStageId) <= 3 && Number(destStageId) <= 3) return true;
      return false;
    };

    const originalOpportunities = [...allOpportunityDetails];

    if (isValidMove()) {
      addInQuickLoad(movedOpportunity.OpportunityIterationsID);

      try {
        setAllOpportunityDetails(
          allOpportunityDetails.map(opportunity =>
            opportunity.OpportunityIterationsID === movedOpportunity.OpportunityIterationsID
              ? { ...opportunity, StageID: destStage, StatusID: destStatus }
              : opportunity
          )
        );

        const payload: updateOpportunity = {
          OpportunityIterationsID: movedOpportunity.OpportunityIterationsID,
          StageID: destStage,
          StatusID: destStatus,
          AnalystID: movedOpportunity.AnalystID,
        };

        await bdportalScreenServices.updateBoard(payload, board);
      } catch (error: unknown) {
        setAllOpportunityDetails(originalOpportunities);
        removeFromQuickLoad(movedOpportunity.OpportunityIterationsID);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error(`Error updating board: ${errorMessage}`, {
          position: 'bottom-right',
          autoClose: 5000,
        });
      } finally {
        removeFromQuickLoad(movedOpportunity.OpportunityIterationsID);
      }
    } else {
      toast.error(`Invalid move, requires admin role`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    }
  };

  const openEditOpportunityModal = (boardName: string) => {
    // add parameter in the url logic to be added
    setIteration(boardName);
    setIsEditOpportunityModalOpen(true);
  };

  const closeEditOpportunityModal = (isNewIterationCreated: boolean = false) => {
    setIsEditOpportunityModalOpen(false);
    // if new iteration created fetch the board details again
    if (isNewIterationCreated) {
      fetchDropdownBoardDetails();
      handleFetchAllConceptDetailsForSearch();
    }
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('opportunityIdParam');
    newSearchParams.delete('opportunityIterationIdParam');
    newSearchParams.delete('isIterationOpen');
    setSearchParams(newSearchParams);
  };

  const updateBoard = async (
    StageID?: number,
    StatusID?: number,
    AnalystID?: number,
    opportunityID?: number
  ) => {
    if (!StageID || !StatusID || !AnalystID || !opportunityID) return;
    const lastUpdatedAllOpportunityDetails = deepCopy(allOpportunityDetails);
    try {
      addInAssigneeUpdatingCards(opportunityID);
      const payload: updateOpportunity = {
        OpportunityIterationsID: opportunityID!,
        StageID: StageID,
        StatusID: StatusID,
        AnalystID: AnalystID,
      };
      const updatedOpportunities = allOpportunityDetails.map(opp =>
        opp.OpportunityIterationsID === opportunityID
          ? {
              ...opp,
              ...(StageID !== undefined && { StageID }),
              ...(StatusID !== undefined && { StatusID }),
              ...(AnalystID !== undefined && { AnalystID }),
            }
          : opp
      );
      setAllOpportunityDetails(updatedOpportunities);
      await bdportalScreenServices.updateBoard(payload, board);
    } catch (_error) {
      setAllOpportunityDetails(lastUpdatedAllOpportunityDetails);
      toast.error('Error occured while updating the board');
    } finally {
      removeFromAssigneeUpdatingCards(opportunityID);
    }
  };

  const handleSetItertation = (iteration: string) => {
    setIteration(iteration);
    setBoard(iteration);
  };

  const handleListButtonClick = () => {
    // navigation logi
    navigate('bdportal/optracker/list');
  };

  const handleCardClick = async (opportunityIterationID: number, opportunityID: number) => {
    setIsEditOpportunityModalOpen(true);
    setOpportunityIID(opportunityIterationID);
    setOpportunityID(opportunityID);
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('opportunityIdParam', opportunityID.toString());
    newSearchParams.set('opportunityIterationIdParam', opportunityIterationID.toString());
    setSearchParams(newSearchParams);
  };

  const toggleBoardExpansion = (boardName: string) => {
    setExpandedStageNames(prev =>
      prev.includes(boardName) ? prev.filter(name => name !== boardName) : [...prev, boardName]
    );
  };

  const filterOpportunityIterationsForTheappliedFilters = (
    opportunitIterationsData: OpportunityDetail[],
    stage: number,
    status: number
  ) => {
    let filteredOpportunityIterations: OpportunityDetail[] = [];
    filteredOpportunityIterations = opportunitIterationsData.filter(iterationData => {
      // check the stage and the filter the opportunities on teh basis of the stage
      if (iterationData.StageID !== stage) {
        return false;
      }

      if (iterationData.StatusID !== status) {
        return false;
      }

      // check if the curent iteratino passed the filtere criteria
      if (
        searchKeyword &&
        !iterationData.MoleculeAndForm.toLocaleLowerCase().includes(
          searchKeyword.toLocaleLowerCase()
        )
      ) {
        return false;
      }

      // check if the opportunity type is portfolio opporunity or not
      if (
        filterIsPortfolioOpportunity !== null &&
        Boolean(iterationData.PortfolioCounter) !== filterIsPortfolioOpportunity
      ) {
        return false;
      }

      if (
        filterAnnualRevenueRange &&
        (filterAnnualRevenueRange[0] || filterAnnualRevenueRange[1])
      ) {
        if (
          !(
            (iterationData.EstAnnualRevenue || 0) >= filterAnnualRevenueRange[0] &&
            (iterationData.EstAnnualRevenue || 0) <= filterAnnualRevenueRange[1]
          )
        ) {
          return false;
        }
      }

      // start end dates range filter

      if (filterStartAndEnddates && filterStartAndEnddates[0] && filterStartAndEnddates[1]) {
        const startDate = new Date(filterStartAndEnddates[0] as Date);
        const endDate = new Date(filterStartAndEnddates[1] as Date);
        const createdAt = new Date(iterationData.CreatedAt);
        if (!(createdAt >= startDate && createdAt <= endDate)) {
          return false;
        }
      }

      if (
        !(
          (!filterStage || iterationData.StageID === filterStage) &&
          (!filterStatus || iterationData.StatusID === filterStatus)
        )
      ) {
        return false;
      }
      // check for the unassigned filter
      if (
        filterAnalyst &&
        filterAnalyst.length > 0 &&
        filterAnalyst.indexOf(iterationData.AnalystID ? iterationData.AnalystID : 0) === -1
      ) {
        return false;
      }

      // check for the unassigned filter
      if (
        filterAccManagers &&
        filterAccManagers.length > 0 &&
        filterAccManagers.indexOf(
          iterationData.AccountManagerID ? iterationData.AccountManagerID : 0
        ) === -1
      ) {
        return false;
      }

      // Check if source filter is applied and doesn't match the iteration data
      if (filterSource?.length !== 0 && filterSource?.indexOf(iterationData.SourceID) === -1) {
        return false;
      }

      // Check if primary filter is applied and doesn't match the iteration data
      if (
        filterPrimarySupplier &&
        !(
          iterationData.PrimarySupplierID?.toLocaleLowerCase() ===
          filterPrimarySupplier.toLocaleLowerCase()
        )
      ) {
        return false;
      }

      const agency = iterationData.Agency
        ? JSON.parse(iterationData.Agency.replace(/'/g, '"'))
        : [0];
      if (filterAgency && filterAgency.length > 0) {
        let found = false;
        agency.forEach((agencyInstance: number) => {
          if (filterAgency.indexOf(agencyInstance) !== -1) {
            found = true;
          }
        });
        if (!found) return false;
      }

      return true;
    });
    return filteredOpportunityIterations;
  };

  return {
    userModulesRoles,
    isOpportunityBoardDetailsLoading,
    setBoardDetails,
    boardDetails,
    handleSetItertation,
    checkIfCardIsGettingAssignedUpdated,
    iteration,
    handleListButtonClick,
    assigneeUpdatingCards,
    setOpportunityIID,
    opportunityIID,
    setUpdateBoardPayload,
    updateBoardPayload,
    board,
    filterAnalyst,
    allOpportunityDetails,
    searchKeyword,
    getAllOpportunity,
    getDropdownDetails,
    expandedStageNames,
    stageNames,
    isEditOpportunityModalOpen,
    openEditOpportunityModal,
    closeEditOpportunityModal,
    onDragEnd,
    toggleBoardExpansion,
    handleCardClick,
    updateBoard,
    handleGetDropdownDetails,
    quickLoadCardsLocal,
    quickLoad,
    opportunityID,
    filterOpportunityIterationsForTheappliedFilters,
  };
};
